import { TabContext, TabPanel } from "@mui/lab";
import { Tab } from "@mui/material";
import type { UserVariables } from "components/shared/AgreementModal";
import { StyledTabs } from "components/shared/StyledTabs";
import type { MissingVariable } from "containers/ContractValidationEdit/utils";
import { SmallBodyHighlight } from "helpers/TypographyComponents";
import type { SetStateAction } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { TPortalType } from "store/types";

import type { AgreementWorkTitle } from "./agreementTitleSource";
import { getVariableGroupI18nKey } from "./groupAgreementVariables";
import type { FoundBeneficiaryVariable } from "./VariablesList";
import { AgreementVariablesList } from "./VariablesList";

type ArticleVariablesGroupProps = {
    articlesVariablesList: Record<number, FoundBeneficiaryVariable[]>;
    workTitles: AgreementWorkTitle[];
    portalType: TPortalType;
    onVariableBlur: (variable?: UserVariables) => void;
    isOwner: boolean;
    userVariables: UserVariables | undefined;
    setUserVariables: React.Dispatch<SetStateAction<UserVariables | undefined>>;
    missingVariables: MissingVariable[];
    beneficiariesMissingVariables: MissingVariable[];
};

const isDocumentVariableFilled = (userVariables: UserVariables | undefined, key: string) => {
    const value = userVariables?.[key];
    return value != null && value !== "";
};

export const ArticleVariablesGroup = ({
    articlesVariablesList,
    workTitles,
    portalType,
    onVariableBlur,
    isOwner,
    userVariables,
    setUserVariables,
    missingVariables,
    beneficiariesMissingVariables,
}: ArticleVariablesGroupProps) => {
    const [t] = useTranslation();
    const [articleTab, setArticleTab] = useState("0");
    const articlesInIdOrder = [...workTitles].sort((left, right) => left.id - right.id);
    const articleIndexes = Object.keys(articlesVariablesList)
        .map(Number)
        .sort((left, right) => left - right);
    const groupTitle = t(getVariableGroupI18nKey("title", portalType));

    if (articleIndexes.length === 0) return null;

    const variablesList = (variables: FoundBeneficiaryVariable[]) => (
        <AgreementVariablesList
            variables={variables}
            onVariableBlur={onVariableBlur}
            isOwner={isOwner}
            userVariables={userVariables}
            setUserVariables={setUserVariables}
            hasMultiBeneficiaries
            missingVariables={missingVariables}
            beneficiariesMissingVariables={beneficiariesMissingVariables}
            embedded
        />
    );

    return (
        <div className="agreement-variable-group agreement-variable-group--articles">
            <SmallBodyHighlight color="black">
                {articleIndexes.length === 1 ? articlesInIdOrder[articleIndexes[0]]?.name || groupTitle : groupTitle}
            </SmallBodyHighlight>
            {articleIndexes.length > 1 ? (
                <TabContext value={articleTab}>
                    <StyledTabs
                        variant="scrollable"
                        scrollButtons
                        value={articleTab}
                        className="beneficiaries-variables-tabs"
                        onChange={(_event, tabValue) => setArticleTab(tabValue)}
                    >
                        {articleIndexes.map((index) => {
                            const article = articlesInIdOrder[index];
                            const hasIncompleteVar = articlesVariablesList[index]?.some(
                                (variable) => !isDocumentVariableFilled(userVariables, variable.multiBeneficiaryKey),
                            );
                            return (
                                <Tab
                                    label={
                                        <div className="beneficiary-tab-label">
                                            <SmallBodyHighlight>{article?.name || groupTitle}</SmallBodyHighlight>
                                            {hasIncompleteVar && <div className="variable-dot-danger" />}
                                        </div>
                                    }
                                    value={String(index)}
                                    key={`article-variables-tab-${index}`}
                                    sx={{ textTransform: "none" }}
                                />
                            );
                        })}
                    </StyledTabs>
                    {articleIndexes.map((index) => (
                        <TabPanel
                            value={String(index)}
                            key={`article-variables-panel-${index}`}
                            className="beneficiary-variables-tab-panel"
                        >
                            {variablesList(articlesVariablesList[index] ?? [])}
                        </TabPanel>
                    ))}
                </TabContext>
            ) : (
                variablesList(articlesVariablesList[articleIndexes[0]] ?? [])
            )}
        </div>
    );
};
